import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../App";
import { CheckCircle, AlertCircle, FileSignature, Eraser, Undo2 } from "lucide-react";

const FirmaContrato = () => {
  const { contratoId, token } = useParams();
  const canvasRef = useRef(null);
  const [contrato, setContrato] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [paths, setPaths] = useState([]);
  const [currentPath, setCurrentPath] = useState([]);

  useEffect(() => {
    const fetchContrato = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/firma/${contratoId}/${token}`);
        setContrato(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Error al cargar el contrato");
      } finally {
        setLoading(false);
      }
    };
    fetchContrato();
  }, [contratoId, token]);

  // Setup canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a1a";
    drawGuide(ctx, rect.width, rect.height);
  }, [contrato]);

  const drawGuide = (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#e5e5e5";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(40, h - 40);
    ctx.lineTo(w - 40, h - 40);
    ctx.stroke();
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "12px sans-serif";
    ctx.fillText("Firma aquí", 40, h - 20);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
  };

  const redrawPaths = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    drawGuide(ctx, rect.width, rect.height);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    paths.forEach(path => {
      if (path.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(path[0].x, path[0].y);
      for (let i = 1; i < path.length; i++) {
        ctx.lineTo(path[i].x, path[i].y);
      }
      ctx.stroke();
    });
  }, [paths]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    setCurrentPath([pos]);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);
    setCurrentPath(prev => [...prev, pos]);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e?.preventDefault();
    setIsDrawing(false);
    if (currentPath.length > 1) {
      setPaths(prev => [...prev, currentPath]);
    }
    setCurrentPath([]);
  };

  const clearCanvas = () => {
    setPaths([]);
    setHasDrawn(false);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    drawGuide(ctx, rect.width, rect.height);
  };

  const undoLast = () => {
    setPaths(prev => {
      const newPaths = prev.slice(0, -1);
      if (newPaths.length === 0) setHasDrawn(false);
      return newPaths;
    });
    setTimeout(redrawPaths, 50);
  };

  useEffect(() => {
    if (paths.length > 0) redrawPaths();
  }, [paths, redrawPaths]);

  const handleSign = async () => {
    if (!hasDrawn) return;
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const firmaData = canvas.toDataURL("image/png");
      await axios.post(`${BACKEND_URL}/api/firma/${contratoId}/${token}`, {
        firma_data: firmaData
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Error al firmar");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-red-200 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-900 mb-2">Error</h2>
          <p className="text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  if (signed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-emerald-200 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Contrato Firmado
          </h2>
          <p className="text-stone-600">
            Tu firma ha sido registrada correctamente. Recibirás confirmación por WhatsApp.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <h1 className="text-emerald-900 text-xl tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Celebra<span style={{fontWeight: 700}}>Mid</span>
          </h1>
          <p className="text-xs text-stone-500">Firma Digital de Contrato</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Contract Summary */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FileSignature className="w-6 h-6 text-emerald-900" />
            <h2 className="text-lg font-semibold text-stone-900">Contrato de Servicios</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-stone-500">Cliente</p>
              <p className="font-medium text-stone-900">{contrato.cliente_nombre}</p>
            </div>
            <div>
              <p className="text-stone-500">Evento</p>
              <p className="font-medium text-stone-900">{contrato.tipo_evento}</p>
            </div>
            <div>
              <p className="text-stone-500">Total</p>
              <p className="font-medium text-emerald-900 text-lg">${contrato.total?.toLocaleString()} MXN</p>
            </div>
            <div>
              <p className="text-stone-500">Anticipo</p>
              <p className="font-medium text-stone-900">${contrato.anticipo?.toLocaleString()} MXN</p>
            </div>
          </div>

          {contrato.servicios?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-sm text-stone-500 mb-2">Servicios incluidos:</p>
              <ul className="space-y-1">
                {contrato.servicios.map((s, i) => (
                  <li key={s.nombre || s.servicio || `servicio-${i}`} className="text-sm text-stone-700 flex justify-between">
                    <span>{s.nombre || s.servicio || `Servicio ${i + 1}`}</span>
                    {s.costo && <span className="text-stone-500">${s.costo?.toLocaleString()}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {contrato.condiciones && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-sm text-stone-500 mb-2">Términos y condiciones:</p>
              <p className="text-sm text-stone-700 whitespace-pre-wrap">{contrato.condiciones}</p>
            </div>
          )}
        </div>

        {/* Signature Pad */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-900">Tu Firma</h3>
            <div className="flex gap-2">
              <button
                onClick={undoLast}
                disabled={paths.length === 0}
                className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors px-2 py-1 rounded"
                data-testid="btn-undo-firma"
              >
                <Undo2 className="w-4 h-4" />
                Deshacer
              </button>
              <button
                onClick={clearCanvas}
                className="flex items-center gap-1 text-sm text-stone-500 hover:text-red-600 transition-colors px-2 py-1 rounded"
                data-testid="btn-limpiar-firma"
              >
                <Eraser className="w-4 h-4" />
                Limpiar
              </button>
            </div>
          </div>

          <div className="border-2 border-dashed border-stone-200 rounded-lg overflow-hidden mb-4 touch-none">
            <canvas
              ref={canvasRef}
              className="w-full cursor-crosshair"
              style={{ height: 220, touchAction: "none" }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
          </div>

          <p className="text-xs text-stone-400 text-center mb-4">
            Dibuja tu firma con el dedo o mouse en el área de arriba
          </p>

          <button
            onClick={handleSign}
            disabled={!hasDrawn || signing}
            className="w-full py-3 bg-emerald-900 text-white rounded-lg font-medium text-sm hover:bg-emerald-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="btn-firmar-contrato"
          >
            <FileSignature className="w-5 h-5" />
            {signing ? "Firmando..." : "Firmar Contrato"}
          </button>
        </div>

        <p className="text-xs text-stone-400 text-center mt-6">
          Al firmar, aceptas los términos y condiciones descritos en este contrato.
        </p>
      </main>
    </div>
  );
};

export default FirmaContrato;
